import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef, RowSelectionState, SortingState } from "@tanstack/react-table";
import z from "zod";

import {
  ProfileCareplanPendingActionBadge,
  ProfileMessagesStatusBadge,
  ProfileOriginBadge,
  ProfileStatusBadge,
  YesOrNoBadge,
} from "@/components/profiles";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "@/constants/pagination";
import { QueryBuilderContext } from "@/contexts/query-builder-context";
import { useProfile } from "@/hooks";
import { useTableQueryBuilder } from "@/hooks/use-table-query-builder";
import type { ProfileDataModel } from "@/types/profile";
import { Button, Checkbox, CopyableValue, ScreenContent, ScreenHeader, ScreenTitle } from "@/ui";
import { PaginatedTable } from "@/ui/common/paginated-table";
import { useCopyValueToClipboard } from "@/ui/common/use-copy-value-to-clipboard";
import { HeaderSearchInput } from "@/ui/table/header-search-input";
import { SearchHeaderSelect } from "@/ui/table/search-header-select";
import {
  CAREPLAN_PENDING_ACTION_OPTIONS,
  formatShortDate,
  getOriginLabel,
  MESSAGES_STATUS_OPTIONS,
  ORIGIN_OPTIONS,
  USER_STATUS_OPTIONS,
  USER_TYPE_OPTIONS,
  YES_OR_NO_OPTIONS,
} from "@/utils";

const formatProfileCopyLine = (
  profile: Pick<ProfileDataModel, "email" | "firstName" | "lastName" | "origin">,
) => {
  const { email, firstName, lastName, origin } = profile;
  const parts = [email, `${firstName} ${lastName}`, getOriginLabel(origin)];

  return parts.filter(Boolean).join(", ");
};

const columns: ColumnDef<ProfileDataModel>[] = [
  {
    id: "select",
    header: ({ table }) => {
      const allSelected = table.getIsAllRowsSelected();

      return (
        <div className="flex flex-col gap-1.5">
          <span
            aria-hidden="true"
            className="invisible text-xs font-semibold tracking-wider uppercase"
          >
            Select
          </span>

          <Button
            className="whitespace-nowrap"
            onClick={() => {
              return table.toggleAllRowsSelected(!allSelected);
            }}
            size="sm"
            variant={allSelected ? "secondary" : "outline"}
          >
            {allSelected ? "Deselect all" : "Select all"}
          </Button>
        </div>
      );
    },
    cell: ({ row }) => {
      return (
        <div className="relative z-[1] flex items-center justify-center">
          <Checkbox
            aria-label={`Select ${row.original.firstName} ${row.original.lastName}`}
            checked={row.getIsSelected()}
            className="relative after:absolute after:-inset-3 after:content-['']"
            id={`select-profile-${row.original.id}`}
            onChange={(checked) => {
              return row.toggleSelected(checked);
            }}
            hideMessage
          />
        </div>
      );
    },
  },
  {
    id: "email",
    accessorKey: "email",
    header: (header) => {
      return <HeaderSearchInput header={header} name="email" />;
    },
    cell: ({ row }) => {
      const { email } = row.original;

      return (
        <CopyableValue
          announcedValue={email}
          label="Email, name and source"
          value={formatProfileCopyLine(row.original)}
        >
          {email}
        </CopyableValue>
      );
    },
  },
  {
    id: "messages",
    accessorKey: "messages",
    cell: ({ row }) => {
      return (
        <ProfileMessagesStatusBadge
          hasMessagesPendingToBeReadByAdmins={row.original.hasMessagesPendingToBeReadByAdmins}
        />
      );
    },
    enableSorting: false,
    header: (header) => {
      return (
        <SearchHeaderSelect
          header={header}
          name="Messages status"
          options={MESSAGES_STATUS_OPTIONS}
        />
      );
    },
  },
  {
    id: "firstName",
    accessorKey: "firstName",
    header: (header) => {
      return <HeaderSearchInput header={header} name="first name" />;
    },
  },
  {
    id: "lastName",
    header: (header) => {
      return <HeaderSearchInput header={header} name="last name" />;
    },
    accessorKey: "lastName",
  },
  {
    id: "phone",
    header: (header) => {
      return <HeaderSearchInput header={header} name="phone number" />;
    },
    accessorKey: "phone",
  },
  {
    id: "careplanDays",
    header: "cp days",
    accessorKey: "careplanDays",
  },
  {
    id: "totalDays",
    header: "total days",
    accessorKey: "totalDays",
  },
  {
    id: "origin",
    header: (header) => {
      return <SearchHeaderSelect header={header} name="origin" options={ORIGIN_OPTIONS} />;
    },
    accessorKey: "origin",
    cell: ({ row }) => {
      return <ProfileOriginBadge origin={row.original.origin} />;
    },
  },
  {
    id: "careplanPendingAction",
    header: (header) => {
      return (
        <SearchHeaderSelect
          header={header}
          name="cp pending action"
          options={CAREPLAN_PENDING_ACTION_OPTIONS}
        />
      );
    },
    accessorKey: "careplanPendingAction",
    cell: ({ row }) => {
      return (
        <ProfileCareplanPendingActionBadge pendingAction={row.original.careplanPendingAction} />
      );
    },
  },
  {
    id: "careplanRequiresReview",
    header: (header) => {
      return (
        <SearchHeaderSelect
          header={header}
          name="careplan requires clinician review"
          options={YES_OR_NO_OPTIONS}
        />
      );
    },
    accessorKey: "careplanRequiresReview",
    cell: ({ row }) => {
      return <YesOrNoBadge value={row.original.careplanRequiresReview} />;
    },
  },
  {
    id: "careplanRequiresAdminReview",
    header: (header) => {
      return (
        <SearchHeaderSelect
          header={header}
          name="careplan requires admin review"
          options={YES_OR_NO_OPTIONS}
        />
      );
    },
    accessorKey: "careplanRequiresAdminReview",
    cell: ({ row }) => {
      return <YesOrNoBadge value={row.original.careplanRequiresAdminReview} />;
    },
  },
  {
    id: "isTestUser",
    header: (header) => {
      return <SearchHeaderSelect header={header} name="type" options={USER_TYPE_OPTIONS} />;
    },
    accessorKey: "type",
    cell: ({ row }) => {
      const isTestUser = row.original.isTestUser;

      return isTestUser ? "Test" : "Real";
    },
  },
  {
    id: "status",
    header: (header) => {
      return <SearchHeaderSelect header={header} name="status" options={USER_STATUS_OPTIONS} />;
    },
    accessorKey: "status",
    cell: ({ row }) => {
      return <ProfileStatusBadge status={row.original.status} />;
    },
  },
  {
    id: "createdAt",
    header: "signed up",
    accessorKey: "createdAt",
    cell: ({ row }) => {
      return formatShortDate(row.original.createdAt);
    },
  },
  {
    id: "isLegacy",
    header: (header) => {
      return (
        <SearchHeaderSelect header={header} name="User is Legacy" options={YES_OR_NO_OPTIONS} />
      );
    },
    accessorKey: "status",
    cell: ({ row }) => {
      return <YesOrNoBadge value={row.original.isLegacy} />;
    },
  },
];

const PROFILE_FILTER_KEYS = [
  "email",
  "messages",
  "firstName",
  "lastName",
  "phone",
  "origin",
  "careplanPendingAction",
  "careplanRequiresReview",
  "careplanRequiresAdminReview",
  "isTestUser",
  "status",
  "isLegacy",
] as const;

const ProfilesPage = () => {
  const { queryBuilder, setCurrentPage, setPageSize, setSorting, sortingState } =
    useTableQueryBuilder({ filterKeys: PROFILE_FILTER_KEYS });
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const copyValueToClipboard = useCopyValueToClipboard();

  const { data: profiles, isLoading: isLoadingProfiles } = useProfile().getProfiles({
    queryBuilder,
  });

  const handlePageChange = (page: number) => {
    setRowSelection({});
    setCurrentPage(page);
  };

  const handleSortingChange = (sorting: SortingState) => {
    setRowSelection({});
    setSorting(sorting);
  };

  const handlePageSizeChange = (size: number) => {
    setRowSelection({});
    setPageSize(size);
  };

  const selectedProfiles = (profiles?.data ?? []).filter((profile) => {
    return rowSelection[String(profile.id)];
  });

  const handleCopySelected = () => {
    const text = selectedProfiles
      .map((profile) => {
        return formatProfileCopyLine(profile);
      })
      .join("\n");

    void copyValueToClipboard(text, "Selected patients");
  };

  return (
    <QueryBuilderContext.Provider value={{ builder: queryBuilder }}>
      <ScreenHeader>
        <ScreenTitle>Profiles</ScreenTitle>

        <Button
          disabled={selectedProfiles.length === 0}
          onClick={handleCopySelected}
          variant="secondary"
        >
          {`Copy${selectedProfiles.length ? ` (${selectedProfiles.length})` : ""}`}
        </Button>
      </ScreenHeader>

      <ScreenContent>
        <PaginatedTable
          columns={columns}
          data={profiles?.data}
          getRowId={(profile) => {
            return String(profile.id);
          }}
          isLoadingData={isLoadingProfiles}
          onPageSizeChange={handlePageSizeChange}
          onRowSelectionChange={setRowSelection}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          pagination={profiles?.pagination}
          rowLink={(profile) => {
            return { to: "/profiles/$id", params: { id: profile.id } };
          }}
          rowLinkLabel={(profile) => {
            return `Open ${profile.firstName} ${profile.lastName}`;
          }}
          rowSelection={rowSelection}
          setCurrentPage={handlePageChange}
          setSorting={handleSortingChange}
          sorting={sortingState}
        />
      </ScreenContent>
    </QueryBuilderContext.Provider>
  );
};

const SORTABLE_COLUMN_IDS = [
  "email",
  "firstName",
  "lastName",
  "phone",
  "isTestUser",
  "createdAt",
  "origin",
  "status",
  "isLegacy",
  "careplanRequiresReview",
  "careplanRequiresAdminReview",
  "totalDays",
  "careplanDays",
  "careplanPendingAction",
] as const;

export const Route = createFileRoute("/_private/profiles/")({
  component: ProfilesPage,
  validateSearch: z.object({
    page: z.number().optional(),
    orderBy: z.enum(SORTABLE_COLUMN_IDS).optional().catch(undefined),
    sortDirection: z.enum(["asc", "desc"]).optional().catch(undefined),
    limit: z
      .number()
      .refine((limit): limit is (typeof PAGE_SIZE_OPTIONS)[number] => {
        return PAGE_SIZE_OPTIONS.some((option) => {
          return option === limit;
        });
      })
      .optional()
      .catch(DEFAULT_PAGE_SIZE),
    ...columns.reduce(
      (acc: Record<string, z.ZodOptional<z.ZodUnion<[z.ZodString, z.ZodNumber]>>>, column) => {
        if (column.id) {
          acc[column.id] = z.string().or(z.number().int()).optional();
        }

        return acc;
      },
      {},
    ),
  }),
});
